import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { supabase } from "@/lib/config/supabase";
import apiService from "@/lib/api-functions/api-service";
import { usePlatformTranslation } from "@/hooks/usePlatformTranslation";
import AuthLayout from "./AuthLayout";
import { useAuthTokenHandler } from "./useAuthTokenHandler";

export default function SignupPage() {
  const { t } = usePlatformTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [invitationCode, setInvitationCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [confirmationMessage, setConfirmationMessage] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  // Handle email confirmation tokens
  useAuthTokenHandler({ setLoading, setError, setConfirmationMessage });

  // Pre-fill fields from URL params
  useEffect(() => {
    const codeFromUrl = searchParams.get('invitation_code');
    const emailFromUrl = searchParams.get('email');

    if (codeFromUrl) {
      console.log('✅ Pre-filling invitation code:', codeFromUrl);
      setInvitationCode(codeFromUrl);
    }

    if (emailFromUrl) {
      console.log('✅ Pre-filling email:', emailFromUrl);
      setEmail(emailFromUrl);
    }

    // Clean up URL params after reading them
    if (codeFromUrl || emailFromUrl) {
      searchParams.delete('invitation_code');
      searchParams.delete('email');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setConfirmationMessage("");

    // Validate password
    if (!password.trim()) {
      setError("Please enter a password.");
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError("Password must be at least 6 characters.");
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords don't match.");
      setLoading(false);
      return;
    }

    // Validate invitation code
    if (!invitationCode.trim()) {
      setError("Invitation code is required. Contact your company or info@locanest.app to get one.");
      setLoading(false);
      return;
    }

    if (invitationCode.trim().length !== 8) {
      setError("Invitation code must be 8 characters.");
      setLoading(false);
      return;
    }

    try {
      // Validate invitation code
      console.log("Validating invitation code:", invitationCode.trim());
      const validationResult = await apiService.validateInvitationCode(
        invitationCode.trim(),
        email
      );

      if (!validationResult.success) {
        console.error("Invitation code validation failed:", validationResult.error);
        setError(validationResult.error || "Invalid invitation code");
        setLoading(false);
        return;
      }

      console.log("✅ Invitation code validated:", validationResult.data);

      // Sign up with password
      const { data, error: signUpError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: 'https://app.locanest.app/login',
          data: {
            invitation_code: invitationCode.trim() || undefined
          }
        }
      });

      if (signUpError) {
        console.error("Sign up failed:", signUpError);
        setError(signUpError.message);
        setLoading(false);
        return;
      }

      // Check if email confirmation is required
      if (!data.session && data.user) {
        console.log("✅ User created, email confirmation required");
        setConfirmationMessage(
          "For security reasons, we have sent you an email to confirm your identity. Please check your inbox and click the confirmation link."
        );
        setError("");
        setLoading(false);
        setEmail("");
        setPassword("");
        setConfirmPassword("");
        setInvitationCode("");
        return;
      }

      if (!data.user || !data.session) {
        setError("Failed to create account. Please try again.");
        setLoading(false);
        return;
      }

      console.log("✅ User account created successfully");

      // Store session token
      if (data.session.access_token) {
        apiService.storeToken(data.session.access_token);
      }

      // Create profile
      try {
        await apiService.createProfile({
          user_id: data.user.id,
          email: data.user.email || '',
          full_name: null,
        });
        console.log('Profile created');
      } catch {
        console.log('Profile creation skipped (may already exist)');
      }

      // Redirect to finish-profile
      const params = new URLSearchParams();
      if (invitationCode.trim()) {
        params.append('invitation_code', invitationCode.trim());
      }
      if (email) {
        params.append('email', email);
      }

      const redirectPath = `/finish-profile?${params.toString()}`;
      console.log('🔀 Redirecting to:', redirectPath);
      navigate(redirectPath);

    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Unexpected error";
      console.error("Sign up error:", err);
      setError(errorMessage);
      setLoading(false);
    }
  };

  return (
    <AuthLayout>
      <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">
        Sign up now
      </h2>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Email Input */}
        <div>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('platform.pages.login.email_address')}
            required
            className="w-full h-8 px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
        </div>

        {/* Password Input */}
        <div className="relative">
          <input
            type={showPassword ? "text" : "password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder={t('platform.common.password')}
            required
            className="w-full h-8 px-4 py-3 pr-10 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            {showPassword ? (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            )}
          </button>
        </div>

        {/* Confirm Password Input */}
        <div className="relative">
          <input
            type={showConfirmPassword ? "text" : "password"}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            placeholder={t('platform.pages.login.confirm_password')}
            required
            className="w-full h-8 px-4 py-3 pr-10 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
          <button
            type="button"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            {showConfirmPassword ? (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.98 8.223A10.477 10.477 0 001.934 12C3.226 16.338 7.244 19.5 12 19.5c.993 0 1.953-.138 2.863-.395M6.228 6.228A10.45 10.45 0 0112 4.5c4.756 0 8.773 3.162 10.065 7.498a10.523 10.523 0 01-4.293 5.774M6.228 6.228L3 3m3.228 3.228l3.65 3.65m7.894 7.894L21 21m-3.228-3.228l-3.65-3.65m0 0a3 3 0 10-4.243-4.243m4.242 4.242L9.88 9.88" />
              </svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5">
                <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            )}
          </button>
        </div>

        {/* Invitation Code Input */}
        <div>
          <input
            type="text"
            value={invitationCode}
            onChange={(e) => setInvitationCode(e.target.value.toUpperCase())}
            placeholder={t('platform.pages.login.invitation_code_required')}
            maxLength={8}
            required
            className="w-full h-8 px-4 py-3 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent uppercase font-mono"
          />
          <p className="text-xs text-gray-500 mt-1">
            You need an invite to LocaNest. Contact your company or{' '}
            <a href="mailto:info@locanest.app" className="text-purple-600 hover:underline">
              info@locanest.app
            </a>
          </p>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className="w-full h-8 bg-brand-brand_purple text-white font-medium rounded-sm hover:bg-brand-brand_purple focus:outline-none focus:ring-2 focus:ring-brand-brand_purple focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? "Signing up..." : "Sign up"}
        </button>

        {/* Confirmation Message */}
        {confirmationMessage && (
          <div className="text-green-600 text-sm text-center bg-green-50 p-3 rounded-sm border border-green-200">
            {confirmationMessage}
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="text-red-600 text-sm text-center bg-red-50 p-3 rounded-sm border border-red-200">
            {error}
          </div>
        )}

        {/* Links */}
        <div className="flex justify-center text-sm">
          <Link
            to="/login"
            className="text-brand-brand_purple hover:text-purple-700 font-medium"
          >
            Already have an account? Login
          </Link>
        </div>
      </form>
    </AuthLayout>
  );
}

